'use client'

/**
 * Single-flight loader for the Google Maps JS API. Returns a Promise that
 * resolves once `window.google.maps.importLibrary` is available. Concurrent
 * callers share the same in-flight load — we only ever inject one
 * `<script>`, even if several components ask in the same tick.
 */
let loadPromise: Promise<void> | null = null

export interface LoadGoogleMapsOptions {
  apiKey: string
  libraries?: ReadonlyArray<'places' | 'marker' | 'geometry'>
}

// Note: `window.google.maps.importLibrary` is supplied by the loaded
// Google Maps script at runtime — we don't ship typings for it here.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type WindowWithMaps = Window & { google?: { maps?: { importLibrary?: any } } }

export function loadGoogleMaps(opts: LoadGoogleMapsOptions): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('loadGoogleMaps must run on the client'))
  }
  const w = window as WindowWithMaps
  if (typeof w.google?.maps?.importLibrary === 'function') {
    return Promise.resolve()
  }
  if (loadPromise) return loadPromise

  loadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-google-maps-loader]',
    )
    if (existing) {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error('Google Maps script failed to load')))
      return
    }
    const libraries = (opts.libraries ?? ['places']).join(',')
    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${opts.apiKey}&libraries=${libraries}&loading=async`
    script.async = true
    script.defer = true
    script.dataset.googleMapsLoader = '1'
    script.addEventListener('load', () => resolve())
    script.addEventListener('error', () => {
      loadPromise = null
      reject(new Error('Google Maps script failed to load'))
    })
    document.head.appendChild(script)
  })

  return loadPromise
}
